'use client';

// In-app toast stack for saved-view scanner alerts — the visual cue that shows
// up wherever you are in the app (the "push on dashboard too" surface), so a
// match is visible even if you miss the OS notification. Mounted once in the
// app layout; subscribes to the alertToast emitter.

import { useEffect, useState, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Bell, X } from 'lucide-react';
import { subscribeAlertToast, type AlertToastData } from '@/lib/alertToast';

const DISMISS_MS = 7000;
const MAX_VISIBLE = 4;

export default function AlertToastLayer() {
  const router = useRouter();
  const [toasts, setToasts] = useState<AlertToastData[]>([]);
  const timersRef = useRef<number[]>([]);

  useEffect(() => {
    const unsub = subscribeAlertToast((t) => {
      setToasts((prev) => [t, ...prev].slice(0, MAX_VISIBLE));
      const id = window.setTimeout(() => {
        setToasts((prev) => prev.filter((x) => x.id !== t.id));
      }, DISMISS_MS);
      timersRef.current.push(id);
    });
    return () => {
      unsub();
      timersRef.current.forEach((id) => window.clearTimeout(id));
      timersRef.current = [];
    };
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-16 right-4 z-[100] flex flex-col gap-2 w-72 pointer-events-none">
      {toasts.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => {
            dismiss(t.id);
            router.push('/portfolio/scanner');
          }}
          className="pointer-events-auto group text-left w-full rounded-lg border border-indigo-500/40 bg-card-bg/95 backdrop-blur shadow-lg shadow-black/30 overflow-hidden hover:border-indigo-400 transition-colors"
        >
          <div className="flex">
            <div className="w-1 bg-indigo-500 shrink-0" aria-hidden />
            <div className="flex-1 min-w-0 px-3 py-2">
              <div className="flex items-center gap-1.5 mb-0.5">
                <Bell size={11} className="text-indigo-400 shrink-0" />
                <span className="text-[10px] uppercase tracking-wide text-indigo-300 font-semibold truncate">
                  {t.viewName}
                </span>
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label="Dismiss"
                  onClick={(e) => {
                    e.stopPropagation();
                    dismiss(t.id);
                  }}
                  className="ml-auto shrink-0 text-slate-500 hover:text-white transition-colors"
                >
                  <X size={12} />
                </span>
              </div>
              <div className="text-sm font-semibold text-white font-mono truncate">{t.title}</div>
              <div className="text-[11px] text-fg-subtle truncate">{t.body}</div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}
